import { X } from 'lucide-react';
import React, { useEffect } from 'react';
type AlertType = 'success' | 'error' | 'info';

interface AlertBarProps {
	type: 'success' | 'error' | 'info';
	message: string;
	showEventLink?: boolean;
	eventLinkText?: string;
	eventLinkFn?: () => void;
	isVisible: boolean;
	setIsVisible: (visible: boolean) => void;
	autoClose?: boolean;
	autoCloseTime?: number;
}

const alertStyles: Record<AlertType, string> = {
	success: 'bg-green-600 text-white',
	error: 'bg-red-700 text-white',
	info: 'bg-blue-400 text-white',
};

/**
 * AlertBar Component
 *
 * @component
 * @example
 * ```tsx
 * <AlertBar
 *   type="success"
 *   message="Operation successful!"
 *   isVisible={true}
 *   setIsVisible={(value) => {}}
 * />
 * ```
 */
export function AlertBar({
	type,
	message,
	showEventLink = false,
	eventLinkText = '',
	eventLinkFn = () => {},
	isVisible,
	setIsVisible,
	autoClose = false,
	autoCloseTime = 5000,
}: AlertBarProps) {
	useEffect(() => {
		if (autoClose) {
			const timer = setTimeout(() => {
				setIsVisible(false);
			}, autoCloseTime);

			return () => clearTimeout(timer);
		}
	}, [autoClose, autoCloseTime, setIsVisible]);

	if (!isVisible) return null;

	const messageLines = message.split('\n');

	return (
		<div className={`fixed top-0 left-0 right-0 z-50 p-4 ${alertStyles[type]} flex justify-center items-center`}>
			<span className="flex-grow text-center">
				{messageLines.map((line, index) => (
					<React.Fragment key={index}>
						{line}
						{index < messageLines.length - 1 && <br />}
					</React.Fragment>
				))}
				{showEventLink && (
					<a onClick={eventLinkFn} className="cursor-pointer underline ml-1 font-medium">
						{eventLinkText}
					</a>
				)}
			</span>
			<button
				onClick={() => setIsVisible(false)}
				className="text-white hover:text-gray-200 focus:outline-none ml-4 float-right"
				aria-label="Close alert"
			>
				<X size={24} />
			</button>
		</div>
	);
}
